import './ManageTeam.scss'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../store/auth/authSlice'
import React, { useCallback, useMemo, useState } from 'react'
import { useGetUsersQuery } from '../../store/user/user'
import { User } from '../../models/User'
import { TableColumn } from 'react-data-table-component'
import FormattedDate from '../../components/FormattedDate'
import { ActionButton } from '../../components/DataTable/ActionButton'
import { Col, Container, Dropdown, Row } from 'react-bootstrap'
import { CustomDropdownItem } from '../../components/Dropdown/CustomDropdownItem'
import { EditUserModal } from './EditUserModal'

import { DeleteUserModal } from './DeleteUserModal'
import { DeactivateUserModal } from './DeactivateUserModal'
import { ZocksDataTable } from '../../components/DataTable/ZocksDataTable'

import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { formatDate } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { Roles } from '../../components/Roles/Roles'
import { isAdmin, isOwner } from '../../common/userUtils'
import { TeamMembersFilter, TeamMembersFilterModel, defaultTeamMembersFilter } from './TeamMembersFilter'
import { getInviteRoleGroups } from './InviteRoleGroups'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useCookies } from 'react-cookie'

const TEAM_MEMBERS_PAGE_SIZE_COOKIE = 'teamMembersPageSize'
const ONE_YEAR_IN_SECONDS = 365 * 24 * 60 * 60
const roleGroups = getInviteRoleGroups()

export const TeamMembersTab = () => {
  const { userId = null, modal = null } = useParams()
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(userId)
  const [showEditUserModal, setShowEditUserModal] = useState<boolean>(modal === 'edit')
  const [showDeleteUserModal, setShowDeleteUserModal] = useState<boolean>(false)
  const [showDeactivateUserModal, setShowDeactivateUserModal] = useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [cookies, setCookie] = useCookies([TEAM_MEMBERS_PAGE_SIZE_COOKIE])

  const parsedPage = Number(searchParams.get('page'))
  const page = Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : Math.floor(parsedPage)

  const parsedSize = Number(searchParams.get('size'))
  const parsedCookieSize = Number(cookies[TEAM_MEMBERS_PAGE_SIZE_COOKIE])
  const perPage =
    !Number.isNaN(parsedSize) && parsedSize >= 1
      ? Math.floor(parsedSize)
      : !Number.isNaN(parsedCookieSize) && parsedCookieSize >= 1
        ? Math.floor(parsedCookieSize)
        : DEFAULT_PAGE_SIZE

  const filter = useMemo<TeamMembersFilterModel>(() => {
    const name = searchParams.get('name')
    const roleGroupsParam = searchParams.get('roleGroups')
    let parsedRoleGroups: string[][] = []
    if (roleGroupsParam) {
      try {
        const parsed = JSON.parse(roleGroupsParam)
        if (Array.isArray(parsed)) {
          parsedRoleGroups = parsed
        }
      } catch {
        parsedRoleGroups = []
      }
    }
    const roleNamesFromUrl = roleGroups
      .filter((rg) => parsedRoleGroups.some((ids) => JSON.stringify([...ids].sort()) === JSON.stringify([...rg.roleIds].sort())))
      .map((rg) => rg.name)
    const statuses = searchParams.get('statuses')
    return {
      namePattern: name || null,
      roleNames: roleNamesFromUrl.length > 0 ? roleNamesFromUrl : defaultTeamMembersFilter.roleNames,
      statuses: statuses ? statuses.split(',') : defaultTeamMembersFilter.statuses,
    }
  }, [searchParams])
  const { hasQuota } = useZocksQuota()
  const userDeactivationEnabled = hasQuota('auth.userDeactivationEnabled.value')
  const selectedRoleGroups = roleGroups.filter((rg) => filter.roleNames.includes(rg.name)).map((rg) => rg.roleIds)
  const {
    data,
    isFetching,
    refetch: refetchUsers,
  } = useGetUsersQuery(
    {
      accountId: accountId,
      pageRequest: { page: page - 1, size: perPage },
      identityTypes: ['idp'],
      name: filter.namePattern ?? undefined,
      statuses: filter.statuses.length > 0 ? filter.statuses : undefined,
      roleGroups: selectedRoleGroups.length > 0 ? selectedRoleGroups : undefined,
    },
    { refetchOnMountOrArgChange: true },
  )

  const selectedUser = data?.data.find((user) => user.id === selectedUserId) ?? null

  const updateSearchParams = useCallback(
    (updater: (prev: URLSearchParams) => URLSearchParams) => {
      setSearchParams(updater, { replace: true })
    },
    [setSearchParams],
  )

  const handleFilterChanged = useCallback(
    (newFilter: TeamMembersFilterModel) => {
      updateSearchParams((prev) => {
        if (newFilter.namePattern) {
          prev.set('name', newFilter.namePattern)
        } else {
          prev.delete('name')
        }
        if (newFilter.roleNames.length > 0) {
          const groups = roleGroups.filter((rg) => newFilter.roleNames.includes(rg.name)).map((rg) => rg.roleIds)
          prev.set('roleGroups', JSON.stringify(groups))
        } else {
          prev.delete('roleGroups')
        }
        if (JSON.stringify(newFilter.statuses) !== JSON.stringify(defaultTeamMembersFilter.statuses)) {
          prev.set('statuses', newFilter.statuses.join(','))
        } else {
          prev.delete('statuses')
        }
        prev.delete('page')
        return prev
      })
    },
    [updateSearchParams],
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams((prev) => {
        if (newPage > 1) {
          prev.set('page', String(newPage))
        } else {
          prev.delete('page')
        }
        return prev
      })
    },
    [updateSearchParams],
  )

  const handlePerRowsChange = useCallback(
    (newPerPage: number, newPage: number) => {
      setCookie(TEAM_MEMBERS_PAGE_SIZE_COOKIE, String(newPerPage), { path: '/', maxAge: ONE_YEAR_IN_SECONDS })
      updateSearchParams((prev) => {
        if (newPerPage !== DEFAULT_PAGE_SIZE) {
          prev.set('size', String(newPerPage))
        } else {
          prev.delete('size')
        }
        if (newPage > 1) {
          prev.set('page', String(newPage))
        } else {
          prev.delete('page')
        }
        return prev
      })
    },
    [updateSearchParams, setCookie],
  )

  const handleOnClickEditUser = (user: User) => {
    setSelectedUserId(user.id)
    setShowEditUserModal(true)
    navigate({ pathname: `/account/manage-team/users/${user.id}/edit`, search: searchParams.toString() }, { replace: true })
  }

  const handleOnClickDeleteUser = (user: User) => {
    setSelectedUserId(user.id)
    setShowDeleteUserModal(true)
  }

  const handleOnClickDeactivateUser = (user: User) => {
    setSelectedUserId(user.id)
    setShowDeactivateUserModal(true)
  }

  const handleEditUserModalHide = () => {
    setShowEditUserModal(false)
    setSelectedUserId(null)
    navigate({ pathname: `/account/manage-team/`, search: searchParams.toString() }, { replace: true })
  }

  const handleUserEdited = () => {
    refetchUsers()
  }

  const handleDeleteUserModalHide = () => {
    setShowDeleteUserModal(false)
    setSelectedUserId(null)
  }

  const handleUserDeleted = () => {
    setShowDeleteUserModal(false)
    setSelectedUserId(null)
    refetchUsers()
  }

  const handleDeactivateUserModalHide = () => {
    setShowDeactivateUserModal(false)
    setSelectedUserId(null)
  }

  const handleUserDeactivated = () => {
    setShowDeactivateUserModal(false)
    setSelectedUserId(null)
    refetchUsers()
  }

  const columns: TableColumn<User>[] = [
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) =>
        row.id === authenticatedUser?.id || isAdmin(authenticatedUser) || isOwner(authenticatedUser) ? (
          <Link className='zocks-text-primary' to={`/account/manage-team/users/${row.id}`}>
            {row.status === 'deactivated' ? `${row.name.replace(/\s\(deactivated\)$/, '')}` : row.name}
          </Link>
        ) : (
          row.name
        ),
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      grow: 2,
      width: '25%',
    },
    {
      name: 'Role',
      selector: (row) => JSON.stringify(row.roles.map((role) => role.id)),
      sortable: true,
      wrap: true,
      cell: (row) => <Roles userRoleIds={row.roles.map((role) => role.id)} />,
    },
    {
      name: 'Date added',
      selector: (row) => (row.createdAt ? formatDate(row.createdAt) : ''),
      sortable: true,
      cell: (row) => <FormattedDate date={row.createdAt ? formatDate(row.createdAt) : ''} />,
      hide: 992,
      grow: 2,
    },
    {
      name: 'Status',
      selector: (row) => (row.status ? row.status : ''),
      sortable: true,
      conditionalCellStyles: [
        {
          when: (row) => row.status === 'active',
          style: {
            color: 'var(--zocks-success)',
          },
        },
        {
          when: (row) => row.status === 'deactivated',
          style: {
            color: 'var(--zocks-gray-500)',
          },
        },
      ],
    },
    {
      ignoreRowClick: true,
      right: true,
      button: true,
      allowOverflow: true,
      width: '3rem',
      cell: (row) => (
        <ActionButton>
          <ScopedBlock
            scope={{
              scope: 'user:update',
              superScope: ['user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickEditUser(row)}>
              <div className='d-flex gap-2 align-items-center py-1 px-2'>
                <i className='bi bi-pencil-square' />
                <span>Edit</span>
              </div>
            </Dropdown.Item>
          </ScopedBlock>
          {userDeactivationEnabled && row.status !== 'deactivated' ? (
            <>
              <Dropdown.Divider className='my-0' />
              <ScopedBlock
                scope={{
                  scope: 'user:delete',
                  superScope: ['user:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeactivateUser(row)}>
                  <div className='d-flex gap-2 align-items-center py-1 px-2'>
                    <i className='bi bi-dash-circle' />
                    <div className='d-flex flex-column'>
                      <span>Deactivate</span>
                      <span className='paragraph-xsmall zocks-text-gray-600'>Keeps user data accessible</span>
                    </div>
                  </div>
                </Dropdown.Item>
              </ScopedBlock>
            </>
          ) : null}
          <ScopedBlock
            scope={{
              scope: 'user:delete',
              superScope: ['user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeleteUser(row)}>
              <div className='d-flex gap-2 align-items-center py-1 px-2'>
                <i className='bi bi-trash' />
                {userDeactivationEnabled && row.status !== 'deactivated' ? (
                  <div className='d-flex flex-column'>
                    <span>Delete</span>
                    <span className='paragraph-xsmall zocks-text-gray-600'>Removes user data permanently</span>
                  </div>
                ) : (
                  <span>Delete</span>
                )}
              </div>
            </Dropdown.Item>
          </ScopedBlock>
        </ActionButton>
      ),
    },
  ]

  return (
    <Container>
      {selectedUserId && showEditUserModal && (
        <EditUserModal userId={selectedUserId} show={showEditUserModal} onHide={handleEditUserModalHide} onUserEdited={handleUserEdited} />
      )}
      {selectedUser ? (
        <>
          <DeleteUserModal user={selectedUser} show={showDeleteUserModal} onHide={handleDeleteUserModalHide} onUserDeleted={handleUserDeleted} />
          {showDeactivateUserModal && (
            <DeactivateUserModal user={selectedUser} onHide={handleDeactivateUserModalHide} onUserDeactivated={handleUserDeactivated} />
          )}
        </>
      ) : null}
      <Row className='mb-3'>
        <Col className='p-0'>
          <TeamMembersFilter filter={filter} onFilterChanged={handleFilterChanged} />
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={data?.data ?? []}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationDefaultPage={page}
            paginationPerPage={perPage}
            paginationTotalRows={data?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
